/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {cloneDeep, merge} from 'lodash';

/**
 * Option Decorators
 *
 * Decorators allow server-side configuration to modify option merging behavior.
 * Each decorator is a function that transforms options based on server config.
 *
 * Decorators are prefixed with @ in server options:
 * ```json
 * {
 *   "video": {
 *     "options": {
 *       "default": {
 *         "loop": false,
 *         "@override": ["loop"],
 *         "@merge": ["params"]
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const optionDecorators = {
  /**
   * @override decorator
   *
   * Override specific properties or entire object.
   * Server value completely replaces local value.
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options
   * @param {boolean|string|string[]} config - Properties to override
   * @returns {object} Modified options
   *
   * @example Override entire object
   * { "@override": true }
   *
   * @example Override specific properties
   * { "@override": "loop" }
   * { "@override": ["loop", "mute"] }
   */
  override(localOptions, serverOptions, config) {
    if (config === true) {
      // Replace entire object
      return serverOptions;
    }

    // Override specific properties
    [].concat(config).forEach((property) => {
      if (Object.hasOwn(serverOptions, property)) {
        localOptions[property] = serverOptions[property];
      } else {
        delete localOptions[property];
      }
    });

    return localOptions;
  },

  /**
   * @merge decorator
   *
   * Deep merge specific properties instead of replacing.
   * Useful for nested objects like params, events, etc.
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options
   * @param {string|string[]} config - Properties to merge
   * @returns {object} Modified options
   *
   * @example Merge params
   * { "@merge": "params" }
   * { "@merge": ["params", "events"] }
   */
  merge(localOptions, serverOptions, config) {
    [].concat(config).forEach((property) => {
      if (Object.hasOwn(serverOptions, property)) {
        localOptions[property] = merge(
          cloneDeep(localOptions[property]),
          serverOptions[property]
        );
      }
    });

    return localOptions;
  },

  /**
   * @extend decorator
   *
   * Extend arrays instead of replacing them.
   * Useful for adding items to arrays without removing existing ones.
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options
   * @param {string|string[]} config - Array properties to extend
   * @returns {object} Modified options
   *
   * @example Extend presets array
   * { "@extend": "presets" }
   * { "@extend": ["presets", "classes"] }
   */
  extend(localOptions, serverOptions, config) {
    [].concat(config).forEach((property) => {
      if (Object.hasOwn(serverOptions, property)) {
        const localValue = localOptions[property];
        const serverValue = serverOptions[property];

        if (Array.isArray(localValue) && Array.isArray(serverValue)) {
          localOptions[property] = [...localValue, ...serverValue];
        } else {
          // Fallback to merge if not arrays
          localOptions[property] = serverValue;
        }
      }
    });

    return localOptions;
  },

  /**
   * @remove decorator
   *
   * Remove specific properties from local options.
   * Useful for disabling features server-side.
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options
   * @param {string|string[]} config - Properties to remove
   * @returns {object} Modified options
   *
   * @example Remove autopilot
   * { "@remove": "autopilot" }
   * { "@remove": ["autopilot", "controls"] }
   */
  remove(localOptions, serverOptions, config) {
    [].concat(config).forEach((property) => {
      delete localOptions[property];
    });

    return localOptions;
  },

  /**
   * @default decorator
   *
   * Set property only if it doesn't exist in local options.
   * Provides default values without overriding existing ones.
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options
   * @param {string|string[]} config - Properties to set default for
   * @returns {object} Modified options
   *
   * @example Set default loop
   * { "loop": true, "@default": "loop" }
   */
  default(localOptions, serverOptions, config) {
    [].concat(config).forEach((property) => {
      if (!Object.hasOwn(localOptions, property) && Object.hasOwn(serverOptions, property)) {
        localOptions[property] = serverOptions[property];
      }
    });

    return localOptions;
  },
};
